import React from 'react';
import { formatCurrency, formatOwnershipAllocation } from '../../utils/formatters';
import { typography } from '../../design-system/tokens/tokens.js';
import {
  buildAssignmentLayout,
  assignmentTotalKey,
  getAccountAssignment,
  PERSONAL_ASSIGNMENT_KEY,
  ENTITY_ASSIGNMENTS_LABEL,
  CONFLICT_PERSONAL_LEDGER,
} from '../../utils/accountAssignment';
import styles from './table-asset-liability.module.css';

// Title now resolves to the entity allocation (title[]) when present, falling
// back to the legacy free-text account_holder. A trailing "*" + tooltip flags a
// title edge still needing review.
//
// This table is the ON-SCREEN MBS / DRFA only (the PDF and Excel builders format
// their own Title cells), so it opts into each entity's user-defined
// abbreviation. The full legal name stays one hover away via titleTooltip, and
// the exports remain unabbreviated.
const titleDisplay = (acct) => formatOwnershipAllocation(acct?.title, { abbreviate: true }) || acct?.account_holder || '';
const titleFullDisplay = (acct) => formatOwnershipAllocation(acct?.title) || acct?.account_holder || '';
// A title listing a business / other entity alongside another owner is a data
// error (business accounts belong to exactly one business), and so is a ledger
// set to Personal on a business-titled account; both are flagged for review
// like an unreviewed title edge.
const titleHasOwnerConflict = (acct) => getAccountAssignment(acct).conflict;
const titleNeedsReview = (acct) =>
  (Array.isArray(acct?.title) && acct.title.some((t) => t?.needs_review)) || titleHasOwnerConflict(acct);

const TITLE_REVIEW_HINT = 'Title needs review — confirm or edit it on the Account Management page';
const TITLE_CONFLICT_HINT = 'Title lists more than one owner type — a business account should belong to one business only';
const LEDGER_CONFLICT_HINT = 'Ledger is set to Personal but the title is a business — confirm which is right';

// Tooltip carries the unabbreviated title whenever the cell is showing a short
// form, so an abbreviation is never lossy on screen.
const titleTooltip = (acct) => {
  const parts = [];
  const full = titleFullDisplay(acct);
  if (full && full !== titleDisplay(acct)) parts.push(full);
  if (titleHasOwnerConflict(acct)) {
    parts.push(getAccountAssignment(acct).conflictReason === CONFLICT_PERSONAL_LEDGER
      ? LEDGER_CONFLICT_HINT : TITLE_CONFLICT_HINT);
  }
  else if (titleNeedsReview(acct)) parts.push(TITLE_REVIEW_HINT);
  return parts.length ? parts.join(' — ') : undefined;
};

// const formatCurrency = (value) => {
//   const absValue = Math.abs(value);
//   const formatted = absValue.toLocaleString('en-US', {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2
//   });
//   return value < 0 ? `(${formatted})` : `${formatted}`;
// };

const SectionHeader = ({ title }) => (
  <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, fontFamily: typography.fontFamily.display }}>
            {title}
          </h2>
        </div>
        );


// `groupByAssignment` (MBS only; the DRFA keeps its court-form layout) splits
// the side into Personal and per-entity groups, each with its own subtotal,
// whenever any account is titled to a business / trust / estate.
export const AssetsLiabilitiesTable = ({ accountType, data, displayOptions, lineNumbers, groupByAssignment = false }) => {
  const _lineNumbers = lineNumbers || {};
  const _displayOptions = displayOptions || {
    showSourceColumns: true,
    showSettlementColumns: true,
    showNotes: true,
    isDraft: false
  };

  // Standardized font sizes
  const FONT_SIZES = {
    normal: '12px',      // Standard text
    percentage: '11px',  // Percentage text under values
    notes: '12px'        // Notes text
  };

  const calculateAccountNetValue = (account) => {
    let netValue = account.latest_balance.end_balance || 0;
    if (account.associated_liabilities) {
      netValue += account.associated_liabilities.reduce((sum, liab) => sum + (liab.latest_balance.end_balance || 0), 0);
    }
    return netValue;
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const calculateSectionTotal = (section) => {
    const accounts = section.accounts || [];
    return accounts.reduce((sum, account) => {
      return sum + calculateAccountNetValue(account);
    }, 0);
  };

  const calculateSectionSourceTotals = (section) => {
    const accounts = section.accounts || [];
    let petitionerTotal = 0;
    let respondentTotal = 0;
    let otherTotal = 0;
    let maritalTotal = 0;
    let petitionerSettlementTotal = 0;
    let respondentSettlementTotal = 0;

    accounts.forEach(account => {
      const sourceValues = calculateSourceValues(account);
      petitionerTotal += sourceValues.petitionerValue;
      respondentTotal += sourceValues.respondentValue;
      otherTotal += sourceValues.otherValue;
      maritalTotal += sourceValues.maritalValue;
      petitionerSettlementTotal += sourceValues.petitionerSettlementValue;
      respondentSettlementTotal += sourceValues.respondentSettlementValue;

      // Add associated liabilities
      if (account.associated_liabilities) {
        account.associated_liabilities.forEach(liability => {
          // Use parent account percentages for liabilities
          const liabilityValue = liability.latest_balance.end_balance || 0;
          petitionerTotal += liabilityValue * sourceValues.petitionerSource;
          respondentTotal += liabilityValue * sourceValues.respondentSource;
          otherTotal += liabilityValue * sourceValues.otherSource;
          const incrementalMaritalValue = liabilityValue * sourceValues.maritalSource;
          maritalTotal += incrementalMaritalValue;
          const incrementalPetitionerSettlement = incrementalMaritalValue * sourceValues.petitionerSettlement;
          petitionerSettlementTotal += incrementalPetitionerSettlement;
          const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
          respondentSettlementTotal += incrementalRespondentSettlement;
        });
      }
    });

    return { petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal };
  };

  // Calculate equity source values for accounts with associated liabilities
  const calculateEquitySourceValues = (account) => {
    const accountSourceValues = calculateSourceValues(account);
    let totalPetitionerValue = accountSourceValues.petitionerValue;
    let totalRespondentValue = accountSourceValues.respondentValue;
    let totalMaritalValue = accountSourceValues.maritalValue;
    let totalPetitionerSettlementValue = accountSourceValues.petitionerSettlementValue;
    let totalRespondentSettlementValue = accountSourceValues.respondentSettlementValue;

    if (account.associated_liabilities) {
      account.associated_liabilities.forEach(liability => {
        // Apply parent account percentages to liability
        const liabilityValue = liability.latest_balance.end_balance || 0;
        totalPetitionerValue += liabilityValue * accountSourceValues.petitionerSource;
        totalRespondentValue += liabilityValue * accountSourceValues.respondentSource;
        const incrementalMaritalValue = liabilityValue * accountSourceValues.maritalSource;
        totalMaritalValue += incrementalMaritalValue;
        const incrementalPetitionerSettlement = incrementalMaritalValue * accountSourceValues.petitionerSettlement;
        totalPetitionerSettlementValue += incrementalPetitionerSettlement;
        const incrementalRespondentSettlement = incrementalMaritalValue - incrementalPetitionerSettlement;
        totalRespondentSettlementValue += incrementalRespondentSettlement;
      });
    }

    return {
      petitionerValue: totalPetitionerValue,
      respondentValue: totalRespondentValue,
      maritalValue: totalMaritalValue,
      petitionerSettlementValue: totalPetitionerSettlementValue,
      respondentSettlementValue: totalRespondentSettlementValue
    };
  };

  const calculateSourceValues = (account) => {
    const end_balance = account.latest_balance.end_balance || 0;
    const petitionerSource = account._petitioner_owning_pct || 0;
    const petitionerValue = account.latest_balance._petitioner_owning_value ?? end_balance * petitionerSource;
    const respondentSource = account._respondent_owning_pct || 0;
    const respondentValue = account.latest_balance._respondent_owning_value ?? end_balance * respondentSource;
    // Non-party "other" stated sources + the marital remainder come from the backend
    // (1.6.3 derive); fall back to 1-pet-res for un-derived accounts (no "other" =>
    // identical). _stated_marital_pct already nets out "other".
    const otherSource = account._stated_other_pct || 0;
    const otherValue = end_balance * otherSource;
    const maritalSource = account._stated_marital_pct ?? (1 - petitionerSource - respondentSource - otherSource);
    const petitionerSettlement = account._petitioner_settlement_pct || 0;
    const maritalValue = end_balance * maritalSource;
    const petitionerSettlementValue = account.latest_balance._petitioner_settlement_value ?? maritalValue * petitionerSettlement;
    const respondentSettlementValue = maritalValue - petitionerSettlementValue;

    return {
      petitionerValue: petitionerValue,
      respondentValue: respondentValue,
      otherValue,
      maritalValue,
      petitionerSource,
      respondentSource,
      otherSource,
      maritalSource,
      petitionerSettlement,
      respondentSettlement: 1 - petitionerSettlement,
      petitionerSettlementValue,
      respondentSettlementValue
    };
  };

  const renderSection = (category, sectionName, section, lineNumbers, keyPrefix = '') => {
    const accounts = section.accounts || [];
    const headers = getTableHeaders(_displayOptions);
    const sectionKey = `${keyPrefix}${sectionName}`;

    return (
      <div key={sectionKey} style={{ marginBottom: '32px' }}>
        {/* <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, fontFamily: typography.fontFamily.display }}>
            {sectionName}
          </h2>
        </div> */}
        <SectionHeader title={sectionName} />

        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #ddd',
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              {headers.map(header => (
                <th key={header.key} style={{
                  border: '1px solid #ddd',
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  width: header.width,
                  fontSize: FONT_SIZES.normal
                }}>
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {accounts.map((account, index) => {
              const accountKey = `${category}-${sectionKey}-${index}-${account.id}`;
              const accountLineNumber = lineNumbers[accountKey];
              const sourceValues = calculateSourceValues(account);

              return (
                <React.Fragment key={account.id}>
                  <tr>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                      {accountLineNumber}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'left', fontSize: FONT_SIZES.normal }}>
                      {account._displayName}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}
                        title={titleTooltip(account)}>
                      {titleDisplay(account)}{titleNeedsReview(account) ? ' *' : ''}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                      {account.latest_balance._source}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                      {formatDate(account.latest_balance.end_date)}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontSize: FONT_SIZES.normal }}>
                      {formatCurrency(account.latest_balance.end_balance)}
                    </td>
                    {_displayOptions.showSourceColumns && (
                      <>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.petitionerValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.petitionerSource)}
                            </div>
                          </div>
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.respondentValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.respondentSource)}
                            </div>
                          </div>
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.otherValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.otherSource)}
                            </div>
                          </div>
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.maritalValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.maritalSource)}
                            </div>
                          </div>
                        </td>
                      </>
                    )}
                    {_displayOptions.showSettlementColumns && (
                      <>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.petitionerSettlementValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.petitionerSettlement)}
                            </div>
                          </div>
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(sourceValues.respondentSettlementValue)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.respondentSettlement)}
                            </div>
                          </div>
                        </td>
                      </>
                    )}
                    {_displayOptions.showNotes && (
                      <td style={{ border: '1px solid #eee', padding: '8px' }}>
                        <div style={{ fontSize: FONT_SIZES.notes, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {account.notes || ''}
                        </div>
                      </td>
                    )}
                  </tr>
                  {account.associated_liabilities?.map(liability => {
                    const liabilityLineNumber = lineNumbers[`${accountKey}-liability-${liability.id}`];
                    // Use parent account percentages for liability calculations
                    const liabilityValue = liability.latest_balance.end_balance || 0;
                    const liabilityPetitionerValue = liabilityValue * sourceValues.petitionerSource;
                    const liabilityRespondentValue = liabilityValue * sourceValues.respondentSource;
                    const liabilityMaritalValue = liabilityValue * sourceValues.maritalSource;
                    const liabilityPetitionerSettlementValue = liabilityMaritalValue * sourceValues.petitionerSettlement;
                    const liabilityRespondentSettlementValue = liabilityMaritalValue - liabilityPetitionerSettlementValue;

                    return (
                      <tr key={liability.id}>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {liabilityLineNumber}
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'left', paddingLeft: '30px', fontStyle: 'italic', fontSize: FONT_SIZES.normal }}>
                          ↳ {liability._displayName}
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}
                            title={titleTooltip(liability)}>
                          {titleDisplay(liability)}{titleNeedsReview(liability) ? ' *' : ''}
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                          {liability.latest_balance._source}
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                          {formatDate(liability.latest_balance.end_date)}
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(liability.latest_balance.end_balance)}
                        </td>
                        {_displayOptions.showSourceColumns && (
                          <>
                            <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                <div style={{ fontSize: FONT_SIZES.normal, color: '#666' }}>
                                  {formatCurrency(liabilityPetitionerValue)}
                                </div>
                                <div style={{ fontSize: FONT_SIZES.percentage, color: '#888', fontStyle: 'italic' }}>
                                  {formatPercentage(sourceValues.petitionerSource)}
                                </div>
                              </div>
                            </td>
                            <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                <div style={{ fontSize: FONT_SIZES.normal, color: '#666' }}>
                                  {formatCurrency(liabilityRespondentValue)}
                                </div>
                                <div style={{ fontSize: FONT_SIZES.percentage, color: '#888', fontStyle: 'italic' }}>
                                  {formatPercentage(sourceValues.respondentSource)}
                                </div>
                              </div>
                            </td>
                            <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                <div style={{ fontSize: FONT_SIZES.normal, color: '#666' }}>
                                  {formatCurrency(liabilityMaritalValue)}
                                </div>
                                <div style={{ fontSize: FONT_SIZES.percentage, color: '#888', fontStyle: 'italic' }}>
                                  {formatPercentage(sourceValues.maritalSource)}
                                </div>
                              </div>
                            </td>
                          </>
                        )}
                        {_displayOptions.showSettlementColumns && (
                          <>
                            <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                <div style={{ fontSize: FONT_SIZES.normal, color: '#666' }}>
                                  {formatCurrency(liabilityPetitionerSettlementValue)}
                                </div>
                                <div style={{ fontSize: FONT_SIZES.percentage, color: '#888', fontStyle: 'italic' }}>
                                  {formatPercentage(sourceValues.petitionerSettlement)}
                                </div>
                              </div>
                            </td>
                            <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                <div style={{ fontSize: FONT_SIZES.normal, color: '#666' }}>
                                  {formatCurrency(liabilityRespondentSettlementValue)}
                                </div>
                                <div style={{ fontSize: FONT_SIZES.percentage, color: '#888', fontStyle: 'italic' }}>
                                  {formatPercentage(sourceValues.respondentSettlement)}
                                </div>
                              </div>
                            </td>
                          </>
                        )}
                        {_displayOptions.showNotes && (
                          <td style={{ border: '1px solid #eee', padding: '8px' }}>
                            <div style={{ fontSize: FONT_SIZES.notes, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {liability.notes || ''}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {/* Add Net Equity row for assets with associated liabilities */}
                  {category === 'assets' && account.associated_liabilities && account.associated_liabilities.length > 0 && (
                    <tr style={{ backgroundColor: '#f0f8ff', fontWeight: 'bold' }}>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                        {lineNumbers[`${accountKey}-equity`]}
                      </td>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'left', paddingLeft: '30px', fontSize: FONT_SIZES.normal }}>
                        {account._displayName} Net Equity
                      </td>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'left', fontSize: FONT_SIZES.normal }}></td>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}></td>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}></td>
                      <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                        {formatCurrency(calculateAccountNetValue(account))}
                      </td>
                      {_displayOptions.showSourceColumns && (
                        <>
                          <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                            {formatCurrency(calculateEquitySourceValues(account).petitionerValue)}
                          </td>
                          <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                            {formatCurrency(calculateEquitySourceValues(account).respondentValue)}
                          </td>
                          <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                            {formatCurrency(calculateEquitySourceValues(account).maritalValue)}
                          </td>
                        </>
                      )}
                      {_displayOptions.showSettlementColumns && (
                        <>
                          <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                            {formatCurrency(calculateEquitySourceValues(account).petitionerSettlementValue)}
                          </td>
                          <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                            {formatCurrency(calculateEquitySourceValues(account).respondentSettlementValue)}
                          </td>
                        </>
                      )}
                      {_displayOptions.showNotes && (
                        <td style={{ border: '1px solid #eee', padding: '8px', fontSize: FONT_SIZES.normal }}></td>
                      )}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {lineNumbers[`${category}-${sectionKey}-total`]}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold', fontSize: FONT_SIZES.normal }} colSpan="4">
                Total {sectionName}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(calculateSectionTotal(section))}
              </td>
              {(() => {
                const sourceTotals = calculateSectionSourceTotals(section);
                return (
                  <>
                    {_displayOptions.showSourceColumns && (
                      <>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.petitionerTotal)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.respondentTotal)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.otherTotal)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.maritalTotal)}
                        </td>
                      </>
                    )}
                    {_displayOptions.showSettlementColumns && (
                      <>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.petitionerSettlementTotal)}
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                          {formatCurrency(sourceTotals.respondentSettlementTotal)}
                        </td>
                      </>
                    )}
                    {_displayOptions.showNotes && (
                      <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: FONT_SIZES.normal }}></td>
                    )}
                  </>
                );
              })()}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const sumGroupTotals = (sections) => {
    const totals = {
      value: 0, petitionerTotal: 0, respondentTotal: 0, otherTotal: 0,
      maritalTotal: 0, petitionerSettlementTotal: 0, respondentSettlementTotal: 0,
    };
    Object.values(sections).forEach((section) => {
      totals.value += calculateSectionTotal(section);
      const st = calculateSectionSourceTotals(section);
      Object.keys(st).forEach((k) => { totals[k] += st[k]; });
    });
    return totals;
  };

  const sideLabel = accountType === 'assets' ? 'Assets' : 'Liabilities';
  const layout = buildAssignmentLayout(data[accountType], { enabled: groupByAssignment });
  const firstEntityIndex = layout.findIndex((g) => g.assignment && g.assignment.key !== PERSONAL_ASSIGNMENT_KEY);

  return (
    <>
      {layout.map(({ assignment, keyPrefix, sections }, groupIndex) => {
        const renderedSections = Object.entries(sections).map(([sectionName, section]) =>
          renderSection(accountType, sectionName, section, _lineNumbers, keyPrefix)
        );
        if (!assignment) return <React.Fragment key="all">{renderedSections}</React.Fragment>;

        const totals = sumGroupTotals(sections);
        return (
          <section key={assignment.key} className={styles.assignmentGroup} aria-label={`${assignment.label} ${sideLabel}`}>
            {groupIndex === firstEntityIndex && (
              <h2 className={styles.entitiesHeader}>{ENTITY_ASSIGNMENTS_LABEL}</h2>
            )}
            <h3 className={styles.assignmentHeader}>
              {assignment.label}
              {assignment.kind && <span className={styles.kindTag}>{assignment.kind}</span>}
            </h3>
            {renderedSections}
            <TotalsTable
              displayOptions={_displayOptions}
              title={`Total ${assignment.label} ${sideLabel}`}
              lineNumber={_lineNumbers[assignmentTotalKey(accountType, assignment)]}
              value={totals.value}
              petitionerTotal={totals.petitionerTotal}
              respondentTotal={totals.respondentTotal}
              otherTotal={totals.otherTotal}
              maritalTotal={totals.maritalTotal}
              petitionerSettlementTotal={totals.petitionerSettlementTotal}
              respondentSettlementTotal={totals.respondentSettlementTotal}
            />
          </section>
        );
      })}
    </>
  );
};

// Helper function to get table headers based on display options
// Helper function to get table headers based on display options
export const getTableHeaders = (displayOptions) => {
  const headers = [
    { key: 'number', label: '#', width: '4%' },
    { key: 'account', label: 'Account', width: '18%' },
    { key: 'title', label: 'Title', width: '9%' },
    { key: 'source', label: 'Source', width: '8%' },
    { key: 'date', label: 'Date', width: '7%' },
    { key: 'value', label: 'Value', width: '7%' }
  ];

  if (displayOptions.showSourceColumns) {
    headers.push(
      { key: 'petitioner-source', label: 'Petitioner Source', width: '6%' },
      { key: 'respondent-source', label: 'Respondent Source', width: '6%' },
      { key: 'other-source', label: 'Other Source', width: '6%' },
      { key: 'marital-source', label: 'Marital Source', width: '6%' }
    );
  }

  if (displayOptions.showSettlementColumns) {
    headers.push(
      { key: 'petitioner-settlement', label: 'Petitioner Settlement', width: '7%' },
      { key: 'respondent-settlement', label: 'Respondent Settlement', width: '7%' }
    );
  }

  if (displayOptions.showNotes) {
    headers.push({ key: 'notes', label: 'Notes', width: '12%' });
  }

  return headers;
};

// Component for rendering totals tables
export const TotalsTable = ({ displayOptions, title, lineNumber, value, petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal }) => {
  // Standardized font sizes - same as in main component
  const FONT_SIZES = {
    normal: '12px', // Standard text
    percentage: '11px', // Percentage text under values
    notes: '12px' // Notes text
  };
  const headers = getTableHeaders(displayOptions);
  return (
    <div key={title} style={{ marginBottom: '32px' }}>
    <SectionHeader title={title} />
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      border: '1px solid #ddd',
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginTop: '16px'
    }}>
      <thead>
        <tr style={{ backgroundColor: '#f5f5f5' }}>
          {headers.map(header => (
            <th key={header.key} style={{
              border: '1px solid #ddd',
              padding: '8px',
              textAlign: 'center',
              fontWeight: 'bold',
              width: header.width,
              fontSize: FONT_SIZES.normal
            }}>
              {header.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
            {lineNumber}
          </td>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold', fontSize: FONT_SIZES.normal }} colSpan="4">
            {title}
          </td>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
            {formatCurrency(value)}
          </td>
          {displayOptions.showSourceColumns && (
            <>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(petitionerTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(respondentTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(otherTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(maritalTotal)}
              </td>
            </>
          )}
          {displayOptions.showSettlementColumns && (
            <>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(petitionerSettlementTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(respondentSettlementTotal)}
              </td>
            </>
          )}
          {displayOptions.showNotes && (
            <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: FONT_SIZES.normal }}></td>
          )}
        </tr>
      </tbody>
    </table>
    </div>
  );
};

export const FinalSettlementTable = ({ displayOptions, title, lineNumbers, value, petitionerTotal, respondentTotal, otherTotal, maritalTotal, petitionerSettlementTotal, respondentSettlementTotal, settlementAdjustment }) => {
// console.log('Rendering FinalSettlementTable with settlementAdjustment:', settlementAdjustment);
  // Standardized font sizes - same as in main component
  const FONT_SIZES = {
    normal: '12px', // Standard text
    percentage: '11px', // Percentage text under values
    notes: '12px' // Notes text
  };
  const headers = getTableHeaders(displayOptions);
  const petitionerFinalAdjustment = settlementAdjustment?.petitioner_final_adjustment || 0;
  const respondentFinalAdjustment = -(settlementAdjustment?.petitioner_final_adjustment || 0);
  const finalPetitionerTotal = petitionerSettlementTotal + petitionerFinalAdjustment;
  const finalRespondentTotal = respondentSettlementTotal + respondentFinalAdjustment;
  const finalAdjustmentNotes = settlementAdjustment?.notes || '';
  return (
    <div key={title} style={{ marginBottom: '32px' }}>
    <SectionHeader title={title} />
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      border: '1px solid #ddd',
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginTop: '16px'
    }}>
      <thead>
        <tr style={{ backgroundColor: '#f5f5f5' }}>
          {headers.map(header => (
            <th key={header.key} style={{
              border: '1px solid #ddd',
              padding: '8px',
              textAlign: 'center',
              fontWeight: 'bold',
              width: header.width,
              fontSize: FONT_SIZES.normal
            }}>
              {header.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
                          <tr>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                      {lineNumbers['final-settlement-adjustment']}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'left', fontSize: FONT_SIZES.normal }} colSpan={displayOptions.showSourceColumns?9:5}>
                      {'Final Settlement Adjustment'}
                    </td>
                    {/* <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                      {account.account_holder}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                      {account.latest_balance._source}
                    </td>
                    <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'center', fontSize: FONT_SIZES.normal }}>
                      {formatDate(account.latest_balance.end_date)}
                    </td> */}
                    {/* <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right', fontSize: FONT_SIZES.normal }}>
                      {formatCurrency(value)}
                    </td> */}
                    {displayOptions.showSourceColumns && (
                      <>
                        {/* <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(petitionerTotal)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(respondentTotal)}
                            </div>
                          </div>
                        </td> */}
                        {/* <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(respondentTotal)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.respondentSource)}
                            </div>
                          </div>
                        </td> */}
                        {/* <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(maritalTotal)}
                            </div>
                            <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.maritalSource)}
                            </div>
                          </div>
                        </td> */}
                      </>
                    )}
                    {displayOptions.showSettlementColumns && (
                      <>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(petitionerFinalAdjustment)}
                            </div>
                            {/* <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.petitionerSettlement)}
                            </div> */}
                          </div>
                        </td>
                        <td style={{ border: '1px solid #eee', padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <div style={{ fontSize: FONT_SIZES.normal, fontWeight: 'bold' }}>
                              {formatCurrency(respondentFinalAdjustment)}
                            </div>
                            {/* <div style={{ fontSize: FONT_SIZES.percentage, color: '#666' }}>
                              {formatPercentage(sourceValues.respondentSettlement)}
                            </div> */}
                          </div>
                        </td>
                      </>
                    )}
                    {displayOptions.showNotes && (
                      <td style={{ border: '1px solid #eee', padding: '8px' }}>
                        <div style={{ fontSize: FONT_SIZES.notes, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                          {finalAdjustmentNotes || ''}
                        </div>
                      </td>
                    )}
                  </tr>
        <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
            {lineNumbers['final-settlement-total']}
          </td>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold', fontSize: FONT_SIZES.normal }} colSpan="4">
            {title}
          </td>
          <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
            {formatCurrency(value)}
          </td>
          {displayOptions.showSourceColumns && (
            <>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(petitionerTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(respondentTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(otherTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(maritalTotal)}
              </td>
            </>
          )}
          {displayOptions.showSettlementColumns && (
            <>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(finalPetitionerTotal)}
              </td>
              <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: FONT_SIZES.normal }}>
                {formatCurrency(finalRespondentTotal)}
              </td>
            </>
          )}
          {displayOptions.showNotes && (
            <td style={{ border: '1px solid #ddd', padding: '8px', fontSize: FONT_SIZES.normal }}></td>
          )}
        </tr>
      </tbody>
    </table>
    </div>
  );
};